import { Button, Modal, Tabs } from '@teo/components';
import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { axios } from '@/axios';
import { config } from '@/config';
import { useGet } from '@/query/fetchHooks';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { ExtraInfoVersion } from './ExtraInfoVersion';
import { SettingsExtraInfo } from './SettingsExtraInfo';

export const ExtraInfoSettings = ({
  closeModal,
  les,
  setIsOpenSettings,
  setLessons,
  setNewName,
  loadVersion,
  coursecontentId,
  setActiveNode,
  selectedEI,
  extraInfoEdit,
}) => {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user;

  const [tab, setTab] = useState('settings');
  const [lesTitle, setLesTitle] = useState(
    les?.name || les?.assetName ? les?.name || les?.assetName : ''
  );

  const [lessonVerified, setLessonVerified] = useState(
    les?.lessonId ? !!les?.lessonVerified : !!les?.directoryVerified
  );
  const [verifiedData, setVerifiedData] = useState(null);

  useGet(`/verifiedlessons`, {
    enabled: !!les?.lessonId,
    lessonId: les?.lessonId,
    organisationId: `${authUser?.activeOrganisationId}${
      (authUser?.role || 0) >= 90 ? `,OR,organisationId=${null}` : ''
    }`,
    onSuccess: async (result) => {
      if (result?.data?.length == 0) return;
      setVerifiedData(result?.data?.[0]);
    },
  });

  const [estimatedTime, setEstimatedTime] = useState(
    les?.estimatedTime ? les?.estimatedTime : 0
  );
  const [selectTags, setSelectTags] = useState([]);
  const [lesTags, setLesTags] = useState([]);
  const [selectCategories, setSelectCategories] = useState([]);
  const [lesCategories, setLesCategories] = useState([]);
  const handleSave = async () => {
    if (les?.lessonId) {
      const actions = [
        axios.put(`${config.backend}/lesson/${les?.lessonId}`, {
          id: les?.id,
          lessonVerified: lessonVerified,
          name: lesTitle,
          estimatedTime: estimatedTime,
        }),
      ];

      if (!lessonVerified && verifiedData) {
        actions.push(
          axios.delete(`${config.backend}/verifiedlesson/${verifiedData?.id}`)
        );
      } else if (lessonVerified && !verifiedData) {
        actions.push(
          axios.post(`${config.backend}/verifiedlessons`, {
            lessonId: les?.lessonId,
            organisationId:
              (authUser?.role || 0) >= 90 &&
              authUser?.activeOrganisationId === 18
                ? null
                : authUser?.activeOrganisationId,
            coursecontentId,
          })
        );
      }

      const [r2, r1, verifiedlessonResult] = await Promise.allSettled(actions);
      if (verifiedlessonResult && verifiedlessonResult.status === 'fulfilled') {
        setVerifiedData(verifiedlessonResult.value.data);
      }
    }

    const promises = [];

    if (
      selectTags?.length > 0 &&
      (les?.lessonId || (!les?.assetId && !les?.lessonId))
    ) {
      for (const addObj2 of selectTags) {
        const existingObj = lesTags?.find((obj1) => obj1?.id === addObj2?.id);
        if (!existingObj) {
          promises.push(
            les?.lessonId &&
              axios.post(`${config.backend}/lessontags`, {
                lessonId: les?.lessonId,
                tag: 'tag:' + addObj2?.label,
                id: addObj2?.id,
              })
          );
        }
      }
    }

    if (
      selectCategories?.length > 0 &&
      (les?.lessonId || (!les?.assetId && !les?.lessonId))
    ) {
      for (const addObj2 of selectCategories) {
        const existingObj = lesCategories?.find(
          (obj1) => obj1?.id === addObj2?.id
        );
        if (!existingObj) {
          les?.lessonId &&
            axios.post(`${config.backend}/lessontags`, {
              lessonId: les?.lessonId,
              tag: 'category:' + addObj2?.label,
              id: addObj2?.id,
            });
        }
      }
    }

    if (
      lesTags?.length > 0 &&
      (les?.lessonId || (!les?.assetId && !les?.lessonId))
    ) {
      for (const removedTag of lesTags) {
        const existingObj = selectTags?.find(
          (obj1) => obj1?.id === removedTag?.id
        );
        if (!existingObj) {
          promises.push(
            axios.delete(
              les?.lessonId &&
                `${config.backend}/lessontag/${les?.lessonId}/${removedTag?.id}`
            )
          );
        }
      }
    }

    if (
      lesCategories?.length > 0 &&
      (les?.lessonId || (!les?.assetId && !les?.lessonId))
    ) {
      for (const removedTag of lesCategories) {
        const existingObj = selectCategories?.find(
          (obj1) => obj1?.id === removedTag?.id
        );
        if (!existingObj) {
          axios.delete(
            les?.lessonId &&
              `${config.backend}/lessontag/${les?.lessonId}/${removedTag?.id}`
          );
        }
      }
    }
    if (les?.lessonId) {
      setLessons((prev) =>
        prev?.map((el) =>
          el.id === les?.id
            ? {
                ...el,
                name: lesTitle,
                lessonVerified: lessonVerified,
                estimatedTime: estimatedTime,
              }
            : el
        )
      );
    }

    Promise.allSettled(promises).then(() => {
      setNewName(lesTitle);
      setIsOpenSettings(false);
    });
  };

  return (
    // <div className="overflow-hidden bg-white">
    <Modal isOpen={loadVersion}>
      <div
        className=""
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <Modal.Header onClose={closeModal}>
          {t('pages.create_edit_course.extra_info_settings')}
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col lg:flex-row">
            <Tabs activeName={tab} onChange={(tab) => setTab(tab)}>
              <Tabs.TabButton name="settings">
                <p className="text-base font-medium">
                  {t('pages.create_edit_course.settings')}
                </p>
              </Tabs.TabButton>
              {les?.lessonId && (
                <Tabs.TabButton name="versions">
                  <p className="text-base font-medium">
                    {t('pages.create_edit_course.versions_title')}
                  </p>
                </Tabs.TabButton>
              )}
            </Tabs>
          </div>
          <div className="-mx-6 mt-5 max-h-[62vh] overflow-y-scroll px-6">
            {tab === 'settings' && (
              <SettingsExtraInfo
                les={les}
                setLesTitle={setLesTitle}
                lesTitle={lesTitle}
                setLessonVerified={setLessonVerified}
                lessonVerified={lessonVerified}
                setEstimatedTime={setEstimatedTime}
                estimatedTime={estimatedTime}
                setSelectTags={setSelectTags}
                selectTags={selectTags}
                setSelectCategories={setSelectCategories}
                selectCategories={selectCategories}
                setLesTags={setLesTags}
                lesCategories={lesCategories}
                setLesCategories={setLesCategories}
                lesTags={lesTags}
              />
            )}
            {tab === 'versions' && (
              <ExtraInfoVersion
                les={les}
                setIsOpenSettings={setIsOpenSettings}
                setActiveNode={setActiveNode}
                selectedEI={selectedEI}
                extraInfoEdit={extraInfoEdit}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer
          cancelButtonLabel={t('button.cancel')}
          onCancel={closeModal}
        >
          <div>
            <Button onClick={() => handleSave()}>{t('button.save')}</Button>
          </div>
        </Modal.Footer>
      </div>
    </Modal>
    // </div>
  );
};
